<script setup lang="ts">
  import { onBeforeUnmount, onMounted } from 'vue';
  import SearchIcon from '@/shared/assets/icons/SearchIcon.svg';
  import OrderIcon from '@/shared/assets/icons/Order.svg';
  import ArrowsIcon from '@/shared/assets/icons/ArrowsIcon.svg';
  import ForwardIcon from '@/shared/assets/icons/ForwardIcon.svg';
  import { IconImage, Tooltip } from '@/shared/ui';

  type RuleSortOption = 'newest' | 'oldest' | 'name-asc' | 'name-desc';

  const props = defineProps<{
    searchTerm: string;
    actionsDisabled: boolean;
    exportDisabled: boolean;
    exporting: boolean;
    toggleLabel: string;
    hasExpandedRule: boolean;
    selectedSortOption: RuleSortOption | null;
    sortActive: boolean;
    sortDropdownOpen: boolean;
  }>();

  const emit = defineEmits<{
    (event: 'update-search', value: string): void;
    (event: 'toggle-all'): void;
    (event: 'update-sort', value: RuleSortOption): void;
    (event: 'update-sort-open', value: boolean): void;
    (event: 'export-rules-list'): void;
  }>();

  const sortOptions: { id: RuleSortOption; label: string }[] = [
    { id: 'newest', label: 'Mais recente' },
    { id: 'oldest', label: 'Mais antigo' },
    { id: 'name-asc', label: 'Ordem alfabética crescente' },
    { id: 'name-desc', label: 'Ordem alfabética decrescente' },
  ];
  function handleSearchInput(event: Event) {
    emit('update-search', (event.target as HTMLInputElement).value);
  }

  function toggleSortDropdown() {
    if (props.actionsDisabled) return;
    emit('update-sort-open', !props.sortDropdownOpen);
  }

  function handleSortSelection(option: RuleSortOption) {
    emit('update-sort', option);
    emit('update-sort-open', false);
  }

  function handleOutsidePointerDown(event: PointerEvent) {
    if (!props.sortDropdownOpen) return;
    const clickedInsideSortControl = event
      .composedPath()
      .some(
        (eventTarget) =>
          eventTarget instanceof HTMLElement &&
          eventTarget.dataset.rulesSortControl !== undefined
      );
    if (clickedInsideSortControl) return;

    emit('update-sort-open', false);
  }

  onMounted(() => {
    document.addEventListener('pointerdown', handleOutsidePointerDown, true);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
  });
</script>

<template>
  <div class="flex flex-col gap-6 border-b border-gray-02 pb-6">
    <label class="relative block">
      <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-03">
        <IconImage :src="SearchIcon" alt="Buscar" />
      </span>
      <input
        :value="searchTerm"
        type="text"
        placeholder="Buscar"
        :disabled="actionsDisabled"
        class="h-[42px] w-full rounded-lg border border-gray-02 bg-white-01 pl-10 pr-3 text-sm text-gray-07 outline-none placeholder:text-gray-03 disabled:cursor-not-allowed disabled:bg-gray-08"
        @input="handleSearchInput"
      />
    </label>

    <div class="flex items-center gap-4">
      <div class="relative" data-rules-sort-control>
        <button
          type="button"
          :disabled="actionsDisabled"
          class="flex h-[42px] items-center justify-center gap-2 rounded-lg border px-4 text-sm font-medium shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] transition-colors disabled:cursor-not-allowed disabled:border-gray-02 disabled:bg-transparent disabled:text-gray-02 disabled:shadow-none"
          :class="
            sortActive
              ? 'border-[var(--theme-secondary-ink)] bg-[var(--theme-secondary)] text-white'
              : 'border-[var(--theme-secondary-ink)] bg-transparent text-[var(--theme-secondary-ink)]'
          "
          aria-haspopup="menu"
          :aria-expanded="sortDropdownOpen"
          @click="toggleSortDropdown"
        >
          <IconImage :src="OrderIcon" alt="Ordenar por" />
          <span>Ordenar por</span>
        </button>

        <div
          v-if="sortDropdownOpen && !actionsDisabled"
          class="absolute left-0 top-[calc(100%+8px)] z-20 flex w-[255px] flex-col rounded-lg border border-gray-02 bg-white-01 p-2 shadow-[0px_8px_24px_rgba(16,24,40,0.12)]"
          role="menu"
          data-testid="rules-sort-dropdown"
        >
          <button
            v-for="option in sortOptions"
            :key="option.id"
            type="button"
            class="rounded-lg px-3 py-2 text-left text-sm font-normal text-gray-07"
            :class="{ 'bg-gray-08': selectedSortOption === option.id }"
            role="menuitemradio"
            :aria-checked="selectedSortOption === option.id"
            @click="handleSortSelection(option.id)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <button
        type="button"
        :disabled="actionsDisabled"
        class="flex h-[42px] items-center justify-center gap-2 rounded-lg border border-[var(--theme-secondary-ink)] px-4 text-sm font-medium text-[var(--theme-secondary-ink)] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] disabled:cursor-not-allowed disabled:border-gray-02 disabled:text-gray-02 disabled:shadow-none"
        @click="emit('toggle-all')"
      >
        <IconImage
          :src="ArrowsIcon"
          :alt="toggleLabel"
          class="transition-transform"
          :class="{ 'rotate-180': !hasExpandedRule }"
        />
        <span>{{ toggleLabel }}</span>
      </button>

      <Tooltip content="Lista de regras" placement="left">
        <button
          type="button"
          :disabled="exportDisabled || exporting"
          class="flex size-[42px] items-center justify-center rounded-lg border border-transparent bg-[var(--theme-secondary)] text-white shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--theme-secondary-ink)] focus:ring-offset-2 disabled:cursor-not-allowed disabled:border-gray-02 disabled:bg-white-01 disabled:text-gray-02 disabled:shadow-none disabled:hover:opacity-100"
          aria-label="Lista de regras"
          @click="emit('export-rules-list')"
        >
          <span>
            <IconImage :src="ForwardIcon" alt="Lista de regras" />
          </span>
        </button>
      </Tooltip>
    </div>
  </div>
</template>
